/**
 * Cell Validation Pipeline
 *
 * Orchestrates the validation of a single CSV cell value against its
 * column definition. Applies transforms, built-in type validation,
 * options list checking, and regex validation in the correct order.
 *
 * Pipeline order:
 * 1. Apply transform (trim, uppercase, lowercase, none)
 * 2. Handle empty non-required cells with defaults
 * 2.5. Required check: if column.required && transformedValue === ''
 * 3. Run built-in type validation
 * 4. If column has options → validate against options list
 * 5. If column has regex → validate against compiled regex
 *
 * Steps 4 and 5 are mutually exclusive — template-validator rejects
 * columns that define both options and regex.
 *
 * This module is pure, synchronous, and side-effect-free.
 */

import type { NormalizedColumnDefinition } from '../types/template';
import { columnTypeValidators } from './column-type-validators';
import { testRegex } from './regex-validator';

/** Machine-readable error code identifying which validation step failed */
export type CellErrorCode = 'required' | 'type' | 'options' | 'regex';

/** Result of validating a single cell */
export interface CellValidationResult {
  /** Whether the cell value is valid */
  valid: boolean;
  /** The value after transform was applied */
  transformedValue: string;
  /** Human-readable error message if invalid */
  error?: string;
  /** Which validation rule failed, or null if valid */
  errorCode: CellErrorCode | null;
}

/**
 * Validates a single cell value against a column definition.
 *
 * @param value - Raw cell value string from PapaParse
 * @param column - Normalized column definition with defaults applied
 * @returns Validation result with transformed value and optional error
 */
export function validateCell(
  value: string,
  column: NormalizedColumnDefinition,
): CellValidationResult {
  // Step 1: Apply transform
  const transformedValue =
    column.transform === 'none'
      ? value
      : applyTransform(value, column.transform);

  // Step 2: Empty non-required cells with a default are valid
  if (
    transformedValue === '' &&
    !column.required &&
    column.default !== undefined
  ) {
    return { valid: true, transformedValue: column.default, errorCode: null };
  }

  // Step 2a: Empty non-required cells without a default are also valid (skip type check)
  if (transformedValue === '' && !column.required) {
    return { valid: true, transformedValue, errorCode: null };
  }

  // Step 2.5: Explicit required check (NEW)
  if (column.required && transformedValue === '') {
    return {
      valid: false,
      transformedValue,
      error: 'Required field is empty',
      errorCode: 'required',
    };
  }

  // Step 3: Built-in type validation
  const typeValidator = columnTypeValidators[column.type];
  if (!typeValidator(transformedValue, column)) {
    const formatHint =
      column.type === 'date' && column.dateFormat
        ? ` (expected format: ${column.dateFormat})`
        : '';
    return {
      valid: false,
      transformedValue,
      error: `Value "${transformedValue}" is not a valid ${column.type}${formatHint}`,
      errorCode: 'type',
    };
  }

  // Step 4: Options list validation.
  // Note: template-validator rejects templates with both options + regex,
  // so a column will never have both at runtime. This and Step 5 are
  // mutually exclusive paths.
  if (column.options !== undefined) {
    if (!column.options.includes(transformedValue)) {
      return {
        valid: false,
        transformedValue,
        error: `Value "${transformedValue}" is not one of the allowed options: ${column.options.join(', ')}`,
        errorCode: 'options',
      };
    }
    return { valid: true, transformedValue, errorCode: null };
  }

  // Step 5: Regex validation (mutually exclusive with options — see Step 4 note)
  if (column.compiledRegex !== undefined) {
    if (!testRegex(transformedValue, column.compiledRegex)) {
      const message =
        column.regex?.message ??
        `Value "${transformedValue}" does not match the required pattern`;
      return {
        valid: false,
        transformedValue,
        error: message,
        errorCode: 'regex',
      };
    }
  }

  return { valid: true, transformedValue, errorCode: null };
}

// ============================================================
// Internal Helpers
// ============================================================

/**
 * Applies the specified transform to a cell value.
 */
function applyTransform(
  value: string,
  transform: 'trim' | 'uppercase' | 'lowercase',
): string {
  switch (transform) {
    case 'trim':
      return value.trim();
    case 'uppercase':
      return value.toUpperCase();
    case 'lowercase':
      return value.toLowerCase();
  }
}
